const USAGE_FIELDS = [
  'inputTokens',
  'outputTokens',
  'cacheReadTokens',
  'cacheWriteTokens',
  'reasoningTokens'
]

const hasUsableContextUsage = usage => Boolean(
  usage && Number(usage.inputTokens || 0) > 0
)

const usageValueForModel = (usage, modelId) => {
  if (!usage || typeof usage !== 'object') return null
  if (hasUsableContextUsage(usage)) return usage
  if (hasUsableContextUsage(usage[modelId])) return usage[modelId]

  const routed = Object.entries(usage).find(([key, value]) => (
    key.endsWith(`:${modelId}`) && hasUsableContextUsage(value)
  ))
  if (routed) return routed[1]

  const values = Object.values(usage).filter(hasUsableContextUsage)
  return values.length === 1 ? values[0] : null
}

const sameUsage = (left, right) => {
  if (!left || !right) return false
  return USAGE_FIELDS.every(field => Number(left[field] || 0) === Number(right[field] || 0))
}

const activeEntries = entries => {
  entries = (entries || []).filter(Boolean)
  let lastCompactionIndex = -1
  for (let index = 0; index < entries.length; index += 1) {
    if (entries[index].compaction) lastCompactionIndex = index
  }
  return {
    entries: lastCompactionIndex >= 0 ? entries.slice(lastCompactionIndex + 1) : entries,
    hadCompaction: lastCompactionIndex >= 0
  }
}

/*
 * A reply's top-level usage is the sum of every provider request made while
 * executing its tool loop. Context occupancy is instead the most recent
 * individual provider request. Archive pointer usage is also a subtotal, not
 * a request measurement, and must never be shown as current context size.
 */
const selectReplyContextUsage = (reply, modelId) => {
  const active = activeEntries(reply && reply.addedToTranscript)
  const aggregate = usageValueForModel(reply && reply.usage, modelId)
  const candidates = active.entries.map(entry => ({
    entry,
    usage: usageValueForModel(entry && entry.usage, modelId)
  })).filter(candidate => candidate.usage)

  let legacyUsage = null
  for (let index = candidates.length - 1; index >= 0; index -= 1) {
    const { entry, usage } = candidates[index]
    if (entry.transcriptArchive) continue
    if (entry.usageKind === 'request') {
      return { usage, blocked: false, source: 'request' }
    }
    const mirroredAggregate = candidates.length > 1 && sameUsage(usage, aggregate)
    if (!mirroredAggregate && !legacyUsage) legacyUsage = usage
  }
  if (legacyUsage) return { usage: legacyUsage, blocked: false, source: 'legacy-request' }

  if (active.hadCompaction) {
    return { usage: null, blocked: true, source: 'compaction' }
  }
  if (candidates.length > 0) {
    // A newer reply exists, but only cumulative archive/mirrored usage is
    // available. Do not fall through to an older request and label it current.
    return { usage: null, blocked: true, source: 'subtotal-only' }
  }
  return aggregate
    ? { usage: aggregate, blocked: false, source: 'legacy-reply' }
    : { usage: null, blocked: false, source: 'unavailable' }
}

module.exports = {
  selectReplyContextUsage,
  usageValueForModel
}
